/**
 * TourWizard.jsx
 * Floating guided-tour card for all Hylas Companion hub users.
 * – Hub-specific multi-step content (DB-overridable via admin Tour Editor)
 * – Skip individual steps or close the whole tour at any time
 * – Persists "done" state to localStorage
 * – Re-triggerable via the exported useTour hook
 */
import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../../lib/api";
import {
  X, ChevronLeft, ChevronRight, SkipForward,
  Truck, Building2, ShieldCheck, User, HelpCircle,
  Star, Search, DollarSign, BarChart2, FileText, Bell,
  CheckSquare, EyeOff, Layers, Map, Lock, Mail,
} from "lucide-react";

const TOUR_KEY = (hub) => `hylas_tour_seen_v2_${hub}`;
const ACCENT   = "#FF4D00";
const GOLD     = "#F5C842";
const TEAL     = "#2DD4BF";

// ── Hub steps ─────────────────────────────────────────────────────────────────
export const STEPS = {
  carrier: [
    {
      icon: Truck,
      title: "Welcome to Carrier Hub",
      body: "You're now part of the Hylas Companion live driver market. Here you can browse verified CDL-A drivers, place competitive bids, and manage your growing fleet — all in one place.",
      scrollTo: null,
      color: ACCENT,
    },
    {
      icon: Search,
      title: "Browse the Driver Board",
      body: "The driver cards on this page show each driver's CDL class, home state, years of experience, and how many carriers are actively competing for them. New drivers are added regularly.",
      scrollTo: "tour-driver-board",
      color: TEAL,
    },
    {
      icon: DollarSign,
      title: "Placing a Bid",
      body: "Click Place Bid on any driver card to open the bid form. Set your placement fee, select a pay program (CPM, percentage, or lease), choose equipment type, and specify the position. You'll be instantly notified if outbid.",
      scrollTo: "tour-driver-board",
      color: GOLD,
    },
    {
      icon: BarChart2,
      title: "Track Your Bids",
      body: "All your active, pending, and past bids live in the My Bids tab below. You can see counter-offers from our team, respond to them, and monitor your win rate at a glance.",
      scrollTo: "tour-carrier-hub",
      color: ACCENT,
    },
    {
      icon: Building2,
      title: "Complete Your Profile",
      body: "Head to the Profile tab to upload your company logo, enter your MC / DOT number, fleet size, home base state, and equipment types. A complete profile boosts driver confidence in your bids.",
      scrollTo: "tour-carrier-hub",
      color: TEAL,
    },
    {
      icon: Bell,
      title: "Stay Notified",
      body: "You'll receive automatic branded emails the moment you're outbid, a counter-offer arrives, or your bid is confirmed. Check your inbox — and add noreply@hylascompanion.com to safe senders.",
      scrollTo: null,
      color: GOLD,
    },
  ],

  recruiter: [
    {
      icon: User,
      title: "Welcome to Recruiter Hub",
      body: "The Recruiter Hub is your command center for incoming driver applications. Review candidates, manage their market status, and route qualified drivers to the live carrier board.",
      scrollTo: null,
      color: ACCENT,
    },
    {
      icon: Layers,
      title: "Driver Applications Pipeline",
      body: "Each row represents a driver application with their CDL class, experience level, home state, and submission timestamp. Click any row to expand and review the full profile before making a decision.",
      scrollTo: "tour-recruiter-hub",
      color: TEAL,
    },
    {
      icon: CheckSquare,
      title: "Approving for the Market",
      body: "Once a driver is qualified, toggle 'Approve for Market' to make them visible to carriers. Their profile goes live on the driver board instantly and carriers can begin bidding within minutes.",
      scrollTo: "tour-recruiter-hub",
      color: GOLD,
    },
    {
      icon: EyeOff,
      title: "Privacy & Document Controls",
      body: "Sensitive driver documents — MVR, PSP, employment history — are hidden from carriers by default. You decide what's shared and when. Protecting drivers until they're fully placed is a core principle here.",
      scrollTo: "tour-recruiter-hub",
      color: ACCENT,
    },
    {
      icon: Map,
      title: "Driver Referral Links",
      body: "Every driver card has a shareable link. Send it via iMessage, WhatsApp or LinkedIn — the link generates a rich OG preview with the driver's photo and position details, making recruitment posts stand out.",
      scrollTo: "tour-driver-board",
      color: TEAL,
    },
  ],

  safety: [
    {
      icon: ShieldCheck,
      title: "Welcome to Safety Hub",
      body: "The Safety Hub is your DOT compliance center. Generate Driver Qualification packets, review violation history, manage consent flags, and ensure every driver meets regulatory standards before placement.",
      scrollTo: null,
      color: ACCENT,
    },
    {
      icon: FileText,
      title: "Generate DQ Packets",
      body: "Select any driver and click Generate DQ Packet to produce a print-ready PDF with their complete Driver Qualification file — employment history, MVR, PSP report, and consent forms — all in one download.",
      scrollTo: "tour-safety-hub",
      color: GOLD,
    },
    {
      icon: Search,
      title: "Review Driver History",
      body: "Each driver profile contains their full DOT violation history, accident records, and employment verification data. Review everything here before approving a driver for the carrier market.",
      scrollTo: "tour-safety-hub",
      color: TEAL,
    },
    {
      icon: EyeOff,
      title: "Consent & Visibility Flags",
      body: "Drivers with flagged violations or incomplete consent are marked with a warning badge. These flags are visible to you but remain hidden from carriers unless you explicitly release them.",
      scrollTo: "tour-safety-hub",
      color: ACCENT,
    },
    {
      icon: Lock,
      title: "Carrier Mirror Sync",
      body: "When a carrier is selected for a driver, their full DQ packet can be automatically pushed to the carrier's Tenstreet dashboard — eliminating duplicate data entry and accelerating the onboarding process.",
      scrollTo: null,
      color: GOLD,
    },
  ],

  driver: [
    {
      icon: User,
      title: "Your Profile is Live",
      body: "Welcome to the Hylas Companion driver market. Your profile is active and verified carriers are already reviewing it. You don't need to do anything — just stay reachable and let the bids come to you.",
      scrollTo: null,
      color: ACCENT,
    },
    {
      icon: DollarSign,
      title: "Your Incoming Bids",
      body: "Check the Bids section to see every offer placed on your profile. Each bid shows the carrier's name, the placement fee they'll pay, the position type (OTR, regional, local), and their driver pay offer.",
      scrollTo: "tour-driver-hub",
      color: GOLD,
    },
    {
      icon: EyeOff,
      title: "Your Privacy is Protected",
      body: "Your sensitive documents — full SSN, complete address, detailed violation history — are hidden from carriers by default. Only the information needed to evaluate your fit is visible during the bid phase.",
      scrollTo: "tour-driver-hub",
      color: TEAL,
    },
    {
      icon: Bell,
      title: "Stay Responsive",
      body: "When a carrier is selected for you, our team will contact you directly using the information from your application. The faster you respond, the smoother your placement process will be.",
      scrollTo: null,
      color: ACCENT,
    },
  ],

  admin: [
    {
      icon: Star,
      title: "Welcome, Admin",
      body: "You have full access to the Hylas Companion platform. The Transfer Board is your primary tool for reviewing all active carrier bids and selecting the winning placement for each driver.",
      scrollTo: null,
      color: ACCENT,
    },
    {
      icon: Truck,
      title: "Transfer Board",
      body: "Each driver row shows all active bids grouped by carrier. Days-on-market badges (green → amber → red) show which listings need attention. Use the Transfer button to confirm a final placement.",
      scrollTo: "tour-admin-board",
      color: GOLD,
    },
    {
      icon: DollarSign,
      title: "Counter-Offers",
      body: "If a carrier's bid isn't quite right, click the counter-offer button on their bid row to suggest a revised placement fee. The carrier is notified by email and can accept or decline.",
      scrollTo: "tour-admin-board",
      color: TEAL,
    },
    {
      icon: Building2,
      title: "Carrier Network",
      body: "The Carrier Network tab shows all registered carriers, their profile completeness, subscription status, and bid activity. Use this to identify high-value relationships and follow up proactively.",
      scrollTo: "tour-carrier-hub",
      color: ACCENT,
    },
    {
      icon: BarChart2,
      title: "Market Metrics",
      body: "The metrics bar at the top of the carrier section shows platform-wide stats: total bids placed, active bids, transfers completed, and revenue. Updated in real time.",
      scrollTo: "tour-carrier-hub",
      color: GOLD,
    },
  ],
};

// ── HUB meta ─────────────────────────────────────────────────────────────────
const HUB_META = {
  carrier:   { label: "Carrier Tour",   Icon: Truck,       accent: ACCENT },
  recruiter: { label: "Recruiter Tour", Icon: User,        accent: TEAL   },
  safety:    { label: "Safety Tour",    Icon: ShieldCheck, accent: GOLD   },
  driver:    { label: "Driver Tour",    Icon: User,        accent: ACCENT },
  admin:     { label: "Admin Tour",     Icon: Star,        accent: GOLD   },
};

// ── Scroll helper ─────────────────────────────────────────────────────────────
function scrollToSection(id) {
  if (!id) return;
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// ── Main Component ────────────────────────────────────────────────────────────
export function TourWizard({ hub, onClose }) {
  const defaultSteps = STEPS[hub] || [];
  const meta   = HUB_META[hub] || { label: "Tour", Icon: HelpCircle, accent: ACCENT };
  // Merge DB overrides on top of default steps
  const [steps, setSteps] = useState(defaultSteps);
  const [step, setStep] = useState(0);

  useEffect(() => {
    api.marketGetTourContent()
      .then((data) => {
        const overrides = (data?.tour_content?.[hub] || []);
        if (!overrides.length) return;
        setSteps(defaultSteps.map((s, i) => ({
          ...s,
          title: overrides[i]?.title || s.title,
          body:  overrides[i]?.body  || s.body,
        })));
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hub]);

  const current = steps[step];
  const StepIcon = current?.icon || HelpCircle;
  const total    = steps.length;

  useEffect(() => {
    if (current?.scrollTo) scrollToSection(current.scrollTo);
  }, [step, current]);

  function next() {
    if (step < total - 1) setStep((s) => s + 1);
    else done();
  }
  function prev() { if (step > 0) setStep((s) => s - 1); }

  function done() {
    localStorage.setItem(TOUR_KEY(hub), "1");
    onClose();
  }

  if (!current || total === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="tour-wizard"
        initial={{ opacity: 0, y: 32, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 32, scale: 0.97 }}
        transition={{ type: "spring", damping: 22, stiffness: 260 }}
        data-testid="tour-wizard"
        style={{
          position: "fixed",
          bottom: 28,
          right: 24,
          width: 340,
          zIndex: 9000,
          background: "#0e0e12",
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 8px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-2.5"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}
        >
          <div className="flex items-center gap-2">
            <meta.Icon className="w-3 h-3 shrink-0" style={{ color: meta.accent }} />
            <span className="font-head text-[9px] uppercase tracking-[0.25em]" style={{ color: meta.accent }}>
              {meta.label}
            </span>
          </div>
          <button
            data-testid="tour-close-btn"
            onClick={done}
            title="Close tour"
            className="flex items-center gap-1.5 transition-opacity hover:opacity-70"
            style={{ color: "rgba(255,255,255,0.25)" }}
          >
            <span className="font-head text-[8px] uppercase tracking-[0.2em]">Close tour</span>
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 px-4 pt-3 pb-1">
          {steps.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              data-testid={`tour-dot-${i}`}
              className="transition-all"
              style={{
                width:  i === step ? 18 : 6,
                height: 4,
                background: i === step ? current.color : i < step ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.1)",
                border: "none",
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
          ))}
          <span className="ml-auto font-head text-[9px] uppercase tracking-[0.2em]"
            style={{ color: "rgba(255,255,255,0.2)" }}>
            {step + 1} / {total}
          </span>
        </div>

        {/* Body */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 14 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -14 }}
            transition={{ duration: 0.18 }}
            className="px-4 pt-3 pb-4"
          >
            {/* Step icon + title */}
            <div className="flex items-start gap-3 mb-2">
              <div
                className="w-8 h-8 flex items-center justify-center shrink-0"
                style={{ background: `${current.color}14`, border: `1px solid ${current.color}30` }}
              >
                <StepIcon className="w-4 h-4" style={{ color: current.color }} />
              </div>
              <h3 className="font-head text-sm font-black text-white leading-tight mt-1.5">
                {current.title}
              </h3>
            </div>

            {/* Description */}
            <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>
              {current.body}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Footer nav */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          {/* Prev */}
          <button
            onClick={prev}
            disabled={step === 0}
            data-testid="tour-prev-btn"
            className="flex items-center gap-1 font-head text-[9px] uppercase tracking-[0.18em] transition-opacity disabled:opacity-20 hover:opacity-70"
            style={{ color: "rgba(255,255,255,0.4)" }}
          >
            <ChevronLeft className="w-3 h-3" /> Prev
          </button>

          {/* Skip step (not shown on last step) */}
          {step < total - 1 && (
            <button
              onClick={() => setStep((s) => s + 1)}
              data-testid="tour-skip-step-btn"
              className="flex items-center gap-1 font-head text-[9px] uppercase tracking-[0.18em] transition-opacity hover:opacity-70"
              style={{ color: "rgba(255,255,255,0.25)" }}
            >
              <SkipForward className="w-3 h-3" /> Skip
            </button>
          )}

          {/* Next / Finish */}
          <button
            onClick={next}
            data-testid="tour-next-btn"
            className="flex items-center gap-1.5 font-head text-[9px] uppercase tracking-[0.18em] py-2 px-3 transition-all hover:brightness-110"
            style={{ background: current.color, color: current.color === GOLD ? "#111" : "#fff" }}
          >
            {step === total - 1 ? "Finish" : "Next"}
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── useTour hook ─────────────────────────────────────────────────────────────
/** Returns [showTour, openTour, closeTour] */
export function useTour(hub) {
  const [show, setShow] = useState(() => {
    if (!hub) return false;
    return localStorage.getItem(TOUR_KEY(hub)) !== "1";
  });

  const open  = useCallback(() => setShow(true),  []);
  const close = useCallback(() => {
    localStorage.setItem(TOUR_KEY(hub), "1");
    setShow(false);
  }, [hub]);

  // Auto-show on first login (hub changes from null → something)
  useEffect(() => {
    if (!hub) return;
    if (localStorage.getItem(TOUR_KEY(hub)) !== "1") setShow(true);
  }, [hub]);

  return [show, open, close];
}
